import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import HomePage from "./pages/Home";
import NewMeetingPage from "./pages/NewMeeting";
import ProcessingPage from "./pages/Processing";
import MeetingDetailPage from "./pages/MeetingDetail";
import LoginPage from "./pages/Login";
import AdminPage from "./pages/Admin";
import { AuthProvider, useAuth } from "./lib/auth";
import type { ReactNode } from "react";
import { useVersionCheck } from "./hooks/useVersionCheck";

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <ProtectedShell />
            </RequireAuth>
          }
        />
      </Routes>
      <NewVersionToast />
    </AuthProvider>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-brand-dark/60 text-sm">
        載入中⋯⋯
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return <>{children}</>;
}

function ProtectedShell() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/new" element={<NewMeetingPage />} />
          <Route path="/meeting/:id/processing" element={<ProcessingPage />} />
          <Route path="/meeting/:id" element={<MeetingDetailPage />} />
          <Route
            path="/admin"
            element={
              <AdminOnly>
                <AdminPage />
              </AdminOnly>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function AdminOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!user?.is_admin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function Header() {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { user, logout } = useAuth();

  return (
    <header className="border-b border-brand-gray/15 bg-brand-cream/80 backdrop-blur sticky top-0 z-40">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-4">
        <Link to="/" className="flex items-baseline gap-2 sm:gap-3 min-w-0 flex-shrink">
          <span className="text-lg sm:text-xl font-black text-brand-dark whitespace-nowrap">麥開會</span>
          <span className="hidden sm:inline-block text-xs uppercase tracking-[0.3em] text-brand-gold font-medium whitespace-nowrap">
            ChillMeet
          </span>
        </Link>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          {!isHome && (
            <Link
              to="/"
              aria-label="會議列表"
              className="inline-flex items-center gap-1.5 text-sm font-medium px-2.5 sm:px-3 py-1.5 rounded-full border border-brand-gray/30 bg-white text-brand-dark hover:border-brand-gold hover:text-brand-gold transition-colors"
            >
              <svg
                className="w-4 h-4 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 10.5L12 3l9 7.5" />
                <path d="M5 9.5V21h14V9.5" />
              </svg>
              <span className="hidden sm:inline">會議列表</span>
            </Link>
          )}
          {user?.is_admin && (
            <Link
              to="/admin"
              className="hidden sm:inline-block text-sm font-medium px-3 py-1.5 rounded-full border border-brand-gray/30 bg-white text-brand-dark hover:border-brand-gold hover:text-brand-gold transition-colors"
            >
              用戶管理
            </Link>
          )}
          {user && (
            <UserBadge
              displayName={user.display_name ?? user.username}
              onLogout={() => void logout()}
            />
          )}
        </div>
      </div>
    </header>
  );
}

function UserBadge({
  displayName,
  onLogout,
}: {
  displayName: string;
  onLogout: () => void;
}) {
  return (
    <div className="flex items-center gap-2 text-xs text-brand-dark/70">
      <span className="hidden sm:inline">{displayName}</span>
      <button
        onClick={onLogout}
        className="px-2.5 py-1 rounded border border-brand-gray/30 bg-white hover:border-brand-gold hover:text-brand-gold transition-colors"
      >
        登出
      </button>
    </div>
  );
}

function NewVersionToast() {
  const { hasNewVersion, reload } = useVersionCheck();
  if (!hasNewVersion) return null;
  return (
    <div
      role="status"
      className="fixed z-50 bottom-5 left-4 right-4 sm:left-auto sm:right-6 sm:w-80 bg-brand-dark text-white px-4 py-3 rounded-lg shadow-xl flex items-center gap-3"
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">有新版本可以更新</div>
        <div className="text-xs opacity-70">按右邊更新，不用清快取</div>
      </div>
      <button
        onClick={reload}
        className="shrink-0 bg-brand-gold hover:bg-brand-gold/90 px-3 py-1.5 text-sm rounded-md font-medium"
      >
        立即更新
      </button>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-brand-gray/15 bg-brand-cream py-6 mt-auto">
      <div className="max-w-5xl mx-auto px-6 text-center text-xs text-brand-dark/50 leading-relaxed">
        <div>麥開會 · ChillMeet</div>
        <div className="mt-1">
          由 <span className="nb">拾光快門</span> · <span className="nb">Dfusion Photography Ltd.</span> 技術支援
        </div>
      </div>
    </footer>
  );
}
